import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Shuffle, RotateCcw, Check, Repeat } from "lucide-react";
import { RichText } from "./RichText";

const FILTERS = [
  { key: "all", label: "Tümü" },
  { key: "new", label: "Yeni" },
  { key: "again", label: "Tekrar Et" },
  { key: "know", label: "Biliyorum" },
];

export default function Flashcards({ course, status, setStatus }) {
  const cards = course.cards;
  const [filter, setFilter] = useState("all");
  const [topic, setTopic] = useState("all");
  const [order, setOrder] = useState(() => cards.map((_, i) => i));
  const [pos, setPos] = useState(0);
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    setOrder(cards.map((_, i) => i));
    setPos(0);
    setFlipped(false);
    setFilter("all");
    setTopic("all");
  }, [cards]);

  const id = (i) => `${course.key}-${i}`;
  const topics = useMemo(() => [...new Set(cards.map((c) => c[2]))], [cards]);

  const visible = useMemo(
    () =>
      order.filter((i) => {
        const st = status[id(i)];
        if (topic !== "all" && cards[i][2] !== topic) return false;
        if (filter === "new") return !st;
        if (filter === "again") return st === "again";
        if (filter === "know") return st === "know";
        return true;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [order, filter, topic, status, cards]
  );

  const safePos = Math.min(pos, Math.max(visible.length - 1, 0));
  const cur = visible[safePos];
  const knowCount = cards.filter((_, i) => status[id(i)] === "know").length;
  const againCount = cards.filter((_, i) => status[id(i)] === "again").length;

  const move = useCallback(
    (d) => {
      if (!visible.length) return;
      setFlipped(false);
      setTimeout(() => setPos((p) => (Math.min(p, visible.length - 1) + d + visible.length) % visible.length), 120);
    },
    [visible.length]
  );

  const mark = (val) => {
    if (cur == null) return;
    setStatus({ ...status, [id(cur)]: val });
    if (filter === "all" || filter === val) move(1);
    else setFlipped(false);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      if (e.code === "Space") { e.preventDefault(); setFlipped((f) => !f); }
      if (e.key === "ArrowRight") move(1);
      if (e.key === "ArrowLeft") move(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move]);

  const shuffle = () => {
    const arr = cards.map((_, i) => i);
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    setOrder(arr);
    setPos(0);
    setFlipped(false);
  };

  const resetAll = () => {
    const next = { ...status };
    cards.forEach((_, i) => delete next[id(i)]);
    setStatus(next);
    setPos(0);
    setFlipped(false);
  };

  return (
    <div>
      <div className="section-head">
        <span className="eyebrow">{course.code} · Bilgi Kartları</span>
        <h1 className="page-title">İnteraktif Bilgi Kartları</h1>
        <p className="page-sub">{cards.length} kart · Karta dokun ya da <kbd>Boşluk</kbd> ile çevir, <kbd>←</kbd> <kbd>→</kbd> ile gez.</p>
      </div>

      <div className="fc-stats">
        <div className="stat"><span className="stat-n">{knowCount}</span><span>Biliyorum</span></div>
        <div className="stat"><span className="stat-n warn">{againCount}</span><span>Tekrar et</span></div>
        <div className="stat"><span className="stat-n muted">{cards.length - knowCount - againCount}</span><span>Yeni</span></div>
        <div className="fc-progress"><div style={{ width: `${(knowCount / cards.length) * 100}%` }} /></div>
      </div>

      <div className="fc-toolbar">
        <div className="chips" data-testid="flashcard-filters">
          {FILTERS.map((f) => (
            <button key={f.key} className={`chip ${filter === f.key ? "active" : ""}`} onClick={() => { setFilter(f.key); setPos(0); setFlipped(false); }} data-testid={`fc-filter-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="fc-tools">
          <select className="select" value={topic} onChange={(e) => { setTopic(e.target.value); setPos(0); }} data-testid="fc-topic-select">
            <option value="all">Tüm konular</option>
            {topics.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <button className="btn btn-ghost" onClick={shuffle} data-testid="fc-shuffle"><Shuffle size={15} /> Karıştır</button>
          <button className="btn btn-ghost" onClick={resetAll} data-testid="fc-reset"><RotateCcw size={15} /> Sıfırla</button>
        </div>
      </div>

      {cur == null ? (
        <div className="empty" data-testid="fc-empty">
          <p>Bu filtrede kart yok.</p>
          <button className="btn btn-accent" onClick={() => setFilter("all")}>Tüm kartları göster</button>
        </div>
      ) : (
        <div className="fc-stage">
          <div className={`flip-card ${flipped ? "flipped" : ""}`} onClick={() => setFlipped((f) => !f)} data-testid="flashcard">
            <div className="flip-inner">
              <div className="flip-face flip-front">
                <div className="fc-meta">
                  <span className="badge">{cards[cur][2]}</span>
                  <span className="fc-count">{safePos + 1} / {visible.length}</span>
                </div>
                <div className="fc-text"><RichText text={cards[cur][0]} /></div>
                <div className="fc-hint">Cevabı görmek için dokun</div>
                {status[id(cur)] && <span className={`fc-state ${status[id(cur)]}`}>{status[id(cur)] === "know" ? "Biliyorum" : "Tekrar et"}</span>}
              </div>
              <div className="flip-face flip-back">
                <div className="fc-meta"><span className="badge badge-inv">Cevap</span><span className="fc-count">{safePos + 1} / {visible.length}</span></div>
                <div className="fc-text fc-answer"><RichText text={cards[cur][1]} /></div>
              </div>
            </div>
          </div>

          <div className="fc-controls">
            <button className="btn btn-icon lg" onClick={() => move(-1)} aria-label="Önceki" data-testid="fc-prev"><ChevronLeft size={20} /></button>
            <button className="btn btn-again" onClick={() => mark("again")} data-testid="fc-again"><Repeat size={16} /> Tekrar Et</button>
            <button className="btn btn-know" onClick={() => mark("know")} data-testid="fc-know"><Check size={16} /> Biliyorum</button>
            <button className="btn btn-icon lg" onClick={() => move(1)} aria-label="Sonraki" data-testid="fc-next"><ChevronRight size={20} /></button>
          </div>
        </div>
      )}
    </div>
  );
}
